/**
 * OAuthCallbackPage - Handles OAuth redirect from Azure
 *
 * This page is loaded in the OAuth popup after the user authorizes.
 * It extracts the authorization code (or error) from the URL and
 * sends it back to the parent window via postMessage.
 */

import { useEffect, useState } from 'react';
import { makeStyles, mergeClasses, tokens, Spinner, Text } from '@fluentui/react-components';
import { CheckmarkCircleFilled, DismissCircleFilled } from '@fluentui/react-icons';
import { OAUTH_CHANNEL_NAME } from '@/services/oauthChannel';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    padding: tokens.spacingHorizontalXXL,
    backgroundColor: tokens.colorNeutralBackground1,
    textAlign: 'center',
  },
  icon: {
    fontSize: '48px',
    marginBottom: tokens.spacingVerticalL,
  },
  successIcon: {
    color: tokens.colorPaletteGreenForeground1,
  },
  errorIcon: {
    color: tokens.colorPaletteRedForeground1,
  },
  title: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
    marginBottom: tokens.spacingVerticalS,
  },
  message: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground2,
    maxWidth: '400px',
  },
  errorMessage: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorPaletteRedForeground1,
    marginTop: tokens.spacingVerticalM,
    padding: tokens.spacingHorizontalM,
    backgroundColor: tokens.colorPaletteRedBackground1,
    borderRadius: tokens.borderRadiusMedium,
    maxWidth: '400px',
    wordBreak: 'break-word',
  },
});

type CallbackStatus = 'processing' | 'success' | 'error' | 'no-opener';

interface CallbackResult {
  status: CallbackStatus;
  error?: string;
}

interface CallbackParams {
  code?: string;
  error?: string;
  errorDescription?: string;
  state?: string;
}

/**
 * Parse OAuth callback parameters from URL
 */
function parseCallbackParams(): CallbackParams {
  // Check both query params and hash params (OAuth can use either)
  const queryParams = new URLSearchParams(window.location.search);
  const hashParams = new URLSearchParams(window.location.hash.slice(1));

  const result: CallbackParams = {};

  const code = queryParams.get('code') ?? hashParams.get('code');
  if (code) result.code = code;

  const error = queryParams.get('error') ?? hashParams.get('error');
  if (error) result.error = error;

  const errorDescription = queryParams.get('error_description') ?? hashParams.get('error_description');
  if (errorDescription) result.errorDescription = errorDescription;

  const state = queryParams.get('state') ?? hashParams.get('state');
  if (state) result.state = state;

  return result;
}



/**
 * Send result back to parent window
 * Uses postMessage when available, falls back to BroadcastChannel when COOP blocks window.opener
 */
function sendToParent(data: Record<string, string | undefined>): boolean {
  let sentViaOpener = false;
  let sentViaBroadcast = false;

  // Try postMessage to opener (works when COOP doesn't block)
  if (window.opener) {
    try {
      window.opener.postMessage(data, window.location.origin);
      sentViaOpener = true;
    } catch {
      // Cross-origin error or other issue
    }
  }

  // Always try BroadcastChannel as fallback (works even when COOP blocks window.opener)
  if ('BroadcastChannel' in window) {
    try {
      const channel = new BroadcastChannel(OAUTH_CHANNEL_NAME);
      channel.postMessage(data);
      channel.close();
      sentViaBroadcast = true;
    } catch {
      // BroadcastChannel not supported or other error
    }
  }

  return sentViaOpener || sentViaBroadcast;
}

export const OAuthCallbackPage = () => {
  const styles = useStyles();
  const [result, setResult] = useState<CallbackResult>({ status: 'processing' });

  useEffect(() => {
    // Parse the callback parameters
    const { code, error, errorDescription, state } = parseCallbackParams();

    // Build the message to send to parent.
    // Always include `type` so the listener can distinguish OAuth callbacks
    // from unrelated postMessage traffic (React DevTools, etc.).
    const message: Record<string, string | undefined> = { type: 'oauth-callback' };
    if (code) message.code = code;
    if (error) message.error = error;
    if (errorDescription) message.error_description = errorDescription;
    if (state) message.state = state;

    // Send the result to parent (via postMessage or BroadcastChannel)
    const sent = sendToParent(message);

    if (!sent) {
      setResult({
        status: 'error',
        error: 'Failed to communicate with the parent window.',
      });
      return;
    }

    // Set appropriate status - note: Azure's consent service may not pass a code
    // when it handles auth internally, so "no code" is actually a success case
    if (error) {
      setResult({
        status: 'error',
        error: errorDescription ?? error,
      });
    } else if (code) {
      setResult({ status: 'success' });
    } else {
      // No code but no error - Azure handled auth internally, this is success
      setResult({ status: 'success' });
    }

    // Auto-close after showing status
    const closeTimer = setTimeout(() => {
      window.close();
    }, 1500);
    return () => clearTimeout(closeTimer);
  }, []);

  return (
    <div className={styles.container}>
      {result.status === 'processing' && (
        <>
          <Spinner size="large" />
          <Text className={styles.title}>Processing...</Text>
          <Text className={styles.message}>
            Completing authentication, please wait.
          </Text>
        </>
      )}

      {result.status === 'success' && (
        <>
          <CheckmarkCircleFilled className={mergeClasses(styles.icon, styles.successIcon)} />
          <Text className={styles.title}>Authentication Complete</Text>
          <Text className={styles.message}>
            You have been successfully authenticated. This window will close automatically.
          </Text>
        </>
      )}

      {result.status === 'error' && (
        <>
          <DismissCircleFilled className={mergeClasses(styles.icon, styles.errorIcon)} />
          <Text className={styles.title}>Authentication Failed</Text>
          <Text className={styles.message}>
            There was a problem completing authentication. This window will close automatically.
          </Text>
          {result.error && (
            <Text className={styles.errorMessage}>{result.error}</Text>
          )}
        </>
      )}

      {result.status === 'no-opener' && (
        <>
          <DismissCircleFilled className={mergeClasses(styles.icon, styles.errorIcon)} />
          <Text className={styles.title}>Invalid Access</Text>
          <Text className={styles.message}>{result.error}</Text>
        </>
      )}
    </div>
  );
};

OAuthCallbackPage.displayName = 'OAuthCallbackPage';
